import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  Search,
  X
} from "lucide-react";
import { markers, OHUHU_SET_LEGEND, ARRTX_SET_LEGEND, SFAIH_SET_LEGEND, CALIART_SETS, BIANYO_SETS, HOTU_SETS, CALM_OVER_CHAOS_SETS, HANGFAN_SETS, SANJOKI_SET_LEGEND, PRISM_SETS, ROYAL_LANGNICKEL_SETS, COPIC_SKETCH_SETS, COPIC_CLASSIC_SETS, COPIC_CIAO_SETS } from "../utils/data";
import ColorSwatch from "./ColorSwatch";
import { useI18n } from "../contexts/I18nContext";
import classNames from "../helpers/classNames";
import { getUserCollection } from "../utils/userCollection";

const getCodesForMarker = (marker, ohuhuMode) => {
  if (!marker) return [];
  const normalize = (code) => {
    if (!code) return "";
    const trimmed = String(code).trim();
    return trimmed === "-" ? "" : trimmed;
  };

  if (marker.brand !== "Ohuhu" && marker.brand !== "Sanjoki") {
    return [normalize(marker.code)].filter(Boolean);
  }

  if (ohuhuMode === "old") {
    return [normalize(marker.code_old)].filter(Boolean);
  }
  if (ohuhuMode === "new") {
    return [normalize(marker.code_new)].filter(Boolean);
  }
  return [normalize(marker.code_old), normalize(marker.code_new)].filter(Boolean);
};

const matchesBrand = (marker, brand) => {
  if (!brand || brand === "All") return true;
  if (brand === "Ohuhu" || brand === "Ohuhu (Old)" || brand === "Ohuhu (New)") {
    return marker.brand === "Ohuhu";
  }
  return marker.brand === brand;
};

const markerKey = (marker = {}) => {
  const code = marker.code || marker.code_old || marker.code_new || marker.hex || marker.name;
  return `${marker.brand || "Unknown"}-${(code || "").toString().trim().toUpperCase()}`;
};

const getCodeSortKey = (code = "", direction = "asc") => {
  const normalized = String(code || "").trim().toUpperCase();
  const numericMatch = normalized.match(/(\d+)/);
  const numericValue = numericMatch ? parseInt(numericMatch[1], 10) : null;
  const fallbackNumeric = numericValue ?? (direction === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
  const alphaValue = normalized.replace(/\d+/g, "");
  return { numeric: fallbackNumeric, alpha: alphaValue, full: normalized };
};

const compareCodes = (codeA, codeB, direction = "asc") => {
  const keyA = getCodeSortKey(codeA, direction);
  const keyB = getCodeSortKey(codeB, direction);

  if (keyA.numeric !== keyB.numeric) {
    return direction === "asc" ? keyA.numeric - keyB.numeric : keyB.numeric - keyA.numeric;
  }

  if (keyA.alpha !== keyB.alpha) {
    return direction === "asc"
      ? keyA.alpha.localeCompare(keyB.alpha)
      : keyB.alpha.localeCompare(keyA.alpha);
  }

  return direction === "asc"
    ? keyA.full.localeCompare(keyB.full)
    : keyB.full.localeCompare(keyA.full);
};

function CustomSelect({ value, onChange, options, placeholder, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = options.find((o) => o.value === value);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        <span className="truncate">{current ? current.label : placeholder}</span>
        <ChevronDown size={12} className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-[70] animate-fadeIn max-h-56 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${value === opt.value ? "text-rose-pastel-600 font-semibold" : "text-slate-700"}`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && <Check size={11} className="text-rose-pastel-500 flex-shrink-0 ml-2" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MarkerSearchModal({
  isOpen,
  onClose,
  brand,
  ohuhuMode,
  onSelectMarker,
  onAddMarkers,
  enableBulkSelect = false
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("All");
  const [ohuhuSetFilter, setOhuhuSetFilter] = useState("All");
  const [arrtxSetFilter, setArrtxSetFilter] = useState("All");
  const [sfaihSetFilter, setSfaihSetFilter] = useState("All");
  const [caliartSetFilter, setCaliartSetFilter] = useState("All");
  const [bianyoSetFilter, setBianyoSetFilter] = useState("All");
  const [hotuSetFilter, setHotuSetFilter] = useState("All");
  const [calmOverChaosSetFilter, setCalmOverChaosSetFilter] = useState("All");
  const [hangfanSetFilter, setHangfanSetFilter] = useState("All");
  const [sanjokiSetFilter, setSanjokiSetFilter] = useState("All");
  const [prismSetFilter, setPrismSetFilter] = useState("All");
  const [royalLangnickelSetFilter, setRoyalLangnickelSetFilter] = useState("All");
  const [copicSketchSetFilter, setCopicSketchSetFilter] = useState("All");
  const [copicClassicSetFilter, setCopicClassicSetFilter] = useState("All");
  const [copicCiaoSetFilter, setCopicCiaoSetFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedMarkers, setSelectedMarkers] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const inputRef = useRef(null);
  const [userCollectionKeys, setUserCollectionKeys] = useState(new Set());

  const selectedKeys = useMemo(() => new Set(selectedMarkers.map(markerKey)), [selectedMarkers]);
  const selectedCount = selectedMarkers.length;
  const canAddAllMarkers = enableBulkSelect && brandFilter !== "All";

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setBrandFilter("All");
    setOhuhuSetFilter("All");
    setArrtxSetFilter("All");
    setSfaihSetFilter("All");
    setCaliartSetFilter("All");
    setBianyoSetFilter("All");
    setHotuSetFilter("All");
    setCalmOverChaosSetFilter("All");
    setHangfanSetFilter("All");
    setSanjokiSetFilter("All");
    setPrismSetFilter("All");
    setRoyalLangnickelSetFilter("All");
    setCopicSketchSetFilter("All");
    setCopicClassicSetFilter("All");
    setCopicCiaoSetFilter("All");
    setSortOrder("asc");
    setSelectedMarkers([]);
    setIsAdding(false);

    // Load user's collection to check for already-added markers
    const userCollection = getUserCollection();
    const userKeys = new Set(userCollection.map(markerKey));
    setUserCollectionKeys(userKeys);
  }, [isOpen, brand]);

  // Always use all markers for the search modal, regardless of current brand context
  // This allows users to add markers from any brand to their collection
  const sourceMarkers = markers;

  const brandOptions = useMemo(() => {
    const uniqueBrands = Array.from(new Set(sourceMarkers.map((m) => m.brand || "Unknown"))).sort((a, b) =>
      a.localeCompare(b)
    );
    return ["All", ...uniqueBrands];
  }, [sourceMarkers]);

  const isOhuhuBrand = brandFilter === "Ohuhu" || brandFilter === "Ohuhu (Old)" || brandFilter === "Ohuhu (New)";
  const isArrtxBrand = brandFilter === "Arrtx";
  const isSfaihBrand = brandFilter === "SFAIH";
  const isCaliartBrand = brandFilter === "Caliart";
  const isBianyoBrand = brandFilter === "Bianyo";
  const isHotuBrand = brandFilter === "Hotu";
  const isCalmOverChaosBrand = brandFilter === "Calm Over Chaos";
  const isHangfanBrand = brandFilter === "Hangfan";
  const isSanjokiBrand = brandFilter === "Sanjoki";
  const isPrismBrand = brandFilter === "Prism";
  const isRoyalLangnickelBrand = brandFilter === "Royal & Langnickel";
  const isCopicSketchBrand = brandFilter === "Copic Sketch";
  const isCopicClassicBrand = brandFilter === "Copic Classic";
  const isCopicCiaoBrand = brandFilter === "Copic Ciao";

  const brandMarkers = useMemo(() => {
    let filtered = sourceMarkers.filter((m) => matchesBrand(m, brandFilter));

    // Apply Ohuhu set filter if applicable
    if (isOhuhuBrand && ohuhuSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(ohuhuSetFilter));
    }

    if (isArrtxBrand && arrtxSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(arrtxSetFilter));
    }

    if (isSfaihBrand && sfaihSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(sfaihSetFilter));
    }

    if (isCaliartBrand && caliartSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(caliartSetFilter));
    }

    if (isBianyoBrand && bianyoSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(bianyoSetFilter));
    }

    if (isHotuBrand && hotuSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(hotuSetFilter));
    }

    if (isCalmOverChaosBrand && calmOverChaosSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(calmOverChaosSetFilter));
    }

    if (isHangfanBrand && hangfanSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(hangfanSetFilter));
    }

    if (isSanjokiBrand && sanjokiSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(sanjokiSetFilter));
    }

    if (isPrismBrand && prismSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(prismSetFilter));
    }

    if (isRoyalLangnickelBrand && royalLangnickelSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(royalLangnickelSetFilter));
    }

    if (isCopicSketchBrand && copicSketchSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(copicSketchSetFilter));
    }

    if (isCopicClassicBrand && copicClassicSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(copicClassicSetFilter));
    }

    if (isCopicCiaoBrand && copicCiaoSetFilter !== "All") {
      filtered = filtered.filter((m) => m.sets && m.sets.includes(copicCiaoSetFilter));
    }

    return filtered;
  }, [brandFilter, ohuhuSetFilter, arrtxSetFilter, sfaihSetFilter, caliartSetFilter, bianyoSetFilter, hotuSetFilter, calmOverChaosSetFilter, hangfanSetFilter, sanjokiSetFilter, prismSetFilter, royalLangnickelSetFilter, copicSketchSetFilter, copicClassicSetFilter, copicCiaoSetFilter, isOhuhuBrand, isArrtxBrand, isSfaihBrand, isCaliartBrand, isBianyoBrand, isHotuBrand, isCalmOverChaosBrand, isHangfanBrand, isSanjokiBrand, isPrismBrand, isRoyalLangnickelBrand, isCopicSketchBrand, isCopicClassicBrand, isCopicCiaoBrand, sourceMarkers]);

  const brandMarkersToAdd = useMemo(() => {
    if (!canAddAllMarkers) return [];
    return brandMarkers.filter((marker) => !userCollectionKeys.has(markerKey(marker)));
  }, [brandMarkers, canAddAllMarkers, userCollectionKeys]);

  const filteredResults = useMemo(() => {
    const cleaned = query.trim().toLowerCase();
    if (!cleaned) return [];

    const results = brandMarkers
      .map((m) => {
        const codes = getCodesForMarker(m, ohuhuMode);
        if (!codes.length) return null;
        const matchedCode = codes.find((code) => code.toLowerCase().startsWith(cleaned));
        if (!matchedCode) return null;
        return { marker: m, codes, matchedCode };
      })
      .filter(Boolean);

    if (!enableBulkSelect) {
      results.sort((a, b) => compareCodes(a.matchedCode, b.matchedCode, sortOrder));
    }

    return results.slice(0, 120);
  }, [brandMarkers, enableBulkSelect, ohuhuMode, query, sortOrder]);

  const handleSelect = async (marker) => {
    if (!marker) return;
    await onSelectMarker?.(marker);
  };

  const handleToggleSelection = (marker) => {
    const key = markerKey(marker);
    // Don't allow toggling markers that are already in collection
    if (userCollectionKeys.has(key)) {
      return;
    }
    setSelectedMarkers((prev) => {
      if (prev.some((m) => markerKey(m) === key)) {
        return prev.filter((m) => markerKey(m) !== key);
      }
      return [...prev, marker];
    });
  };

  const handleAddSelected = async () => {
    if (!selectedCount || isAdding) return;
    setIsAdding(true);
    try {
      if (typeof onAddMarkers === "function") {
        await onAddMarkers(selectedMarkers);
      } else if (typeof onSelectMarker === "function") {
        for (const marker of selectedMarkers) {
          await onSelectMarker(marker);
        }
      }
      setSelectedMarkers([]);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddBrandCollection = async () => {
    if (!brandMarkersToAdd.length || isAdding) return;
    setIsAdding(true);
    try {
      if (typeof onAddMarkers === "function") {
        await onAddMarkers(brandMarkersToAdd);
      } else if (typeof onSelectMarker === "function") {
        for (const marker of brandMarkersToAdd) {
          await onSelectMarker(marker);
        }
      }
      setSelectedMarkers([]);
    } finally {
      setIsAdding(false);
    }
  };

  const handleResultClick = (marker) => {
    if (enableBulkSelect) {
      handleToggleSelection(marker);
    } else {
      handleSelect(marker);
    }
  };

  const handleResultDoubleClick = (marker) => {
    handleSelect(marker);
  };

  const contextBrand = brand && brand !== "All" && brand !== "My Collection" ? brand : null;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm flex items-center justify-center p-4 overscroll-none overflow-hidden animate-fadeOnly"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
      aria-modal="true"
      role="dialog"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] h-[65vh] md:h-[55vh] flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 flex-shrink-0">
          <Search size={18} className="text-rose-pastel-500" />
          <div className="flex-1">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("randomPalette.search.placeholder")}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 font-medium placeholder:text-slate-400 transition-all"
              aria-label={t("randomPalette.search.aria")}
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            aria-label={t("randomPalette.search.close")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 bg-white/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex flex-wrap gap-2">
              <CustomSelect
                className="flex-1 min-w-[140px]"
                value={brandFilter}
                onChange={(val) => { setBrandFilter(val); setOhuhuSetFilter("All"); setArrtxSetFilter("All"); setSfaihSetFilter("All"); setCaliartSetFilter("All"); setBianyoSetFilter("All"); setHotuSetFilter("All"); setCalmOverChaosSetFilter("All"); setHangfanSetFilter("All"); setSanjokiSetFilter("All"); setPrismSetFilter("All"); setRoyalLangnickelSetFilter("All"); setCopicSketchSetFilter("All"); setCopicClassicSetFilter("All"); setCopicCiaoSetFilter("All"); }}
                options={brandOptions.map((b) => ({ value: b, label: b === "All" ? t("brandSelector.allBrands") : b }))}
                placeholder={t("brandSelector.allBrands")}
              />
              {isOhuhuBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={ohuhuSetFilter}
                  onChange={setOhuhuSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(OHUHU_SET_LEGEND).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isArrtxBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={arrtxSetFilter}
                  onChange={setArrtxSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(ARRTX_SET_LEGEND).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isSfaihBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={sfaihSetFilter}
                  onChange={setSfaihSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(SFAIH_SET_LEGEND).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isCaliartBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={caliartSetFilter}
                  onChange={setCaliartSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(CALIART_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isBianyoBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={bianyoSetFilter}
                  onChange={setBianyoSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(BIANYO_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isHotuBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={hotuSetFilter}
                  onChange={setHotuSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(HOTU_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isCalmOverChaosBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={calmOverChaosSetFilter}
                  onChange={setCalmOverChaosSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(CALM_OVER_CHAOS_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isHangfanBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={hangfanSetFilter}
                  onChange={setHangfanSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(HANGFAN_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isSanjokiBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={sanjokiSetFilter}
                  onChange={setSanjokiSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(SANJOKI_SET_LEGEND).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isPrismBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={prismSetFilter}
                  onChange={setPrismSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(PRISM_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isRoyalLangnickelBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={royalLangnickelSetFilter}
                  onChange={setRoyalLangnickelSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(ROYAL_LANGNICKEL_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isCopicSketchBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={copicSketchSetFilter}
                  onChange={setCopicSketchSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(COPIC_SKETCH_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isCopicClassicBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={copicClassicSetFilter}
                  onChange={setCopicClassicSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(COPIC_CLASSIC_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {isCopicCiaoBrand && (
                <CustomSelect
                  className="flex-1 min-w-[120px]"
                  value={copicCiaoSetFilter}
                  onChange={setCopicCiaoSetFilter}
                  options={[{ value: "All", label: "All Sets" }, ...Object.entries(COPIC_CIAO_SETS).map(([k, l]) => ({ value: k, label: l }))]}
                  placeholder="All Sets"
                />
              )}
              {contextBrand && (
                <button
                  type="button"
                  onClick={() => setBrandFilter(contextBrand)}
                  className="mt-2 inline-flex flex-wrap items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 text-rose-pastel-600 text-[11px] font-semibold tracking-[0.24em] uppercase shadow-soft hover:bg-white focus:outline-none focus:ring-2 focus:ring-rose-pastel-200 hidden sm:inline-flex"
                >
                  {t("collectionPage.search.applyBrand")}
                  <span className="tracking-[0.28em]">{contextBrand}</span>
                </button>
              )}
              {contextBrand && (
                <button
                  type="button"
                  onClick={() => setBrandFilter(contextBrand)}
                  className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 text-rose-pastel-600 text-[10px] font-semibold tracking-[0.2em] uppercase shadow-soft hover:bg-white focus:outline-none focus:ring-2 focus:ring-rose-pastel-200 sm:hidden"
                >
                  {t("collectionPage.search.applyBrand")}
                  <span className="tracking-[0.28em]">{contextBrand}</span>
                </button>
              )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-3 bg-slate-50/70">
          {query.trim().length === 0 && (
            <div className="text-center text-slate-500 text-sm py-6">
              {t("randomPalette.search.empty")}
            </div>
          )}

          {query.trim().length > 0 && filteredResults.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-6">
              {t("randomPalette.search.noResults")}
            </div>
          )}

          <div className={classNames("grid gap-3", "grid-cols-4 sm:grid-cols-6")}>
            {filteredResults.map(({ marker }, idx) => {
              const key = markerKey(marker);
              // Only check collection status when in bulk select mode (adding to collection)
              const isInCollection = enableBulkSelect && userCollectionKeys.has(key);
              const isSelected = selectedKeys.has(key);

              return (
                <div
                  key={`${marker.brand}-${marker.name}-${idx}`}
                  className={classNames(
                    "transition-transform relative",
                    enableBulkSelect && "group",
                    isInCollection ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                  )}
                  onClick={() => !isInCollection && handleResultClick(marker)}
                  onDoubleClick={() => !isInCollection && handleResultDoubleClick(marker)}
                >
                  {enableBulkSelect && (
                    <div
                      className={classNames(
                        "absolute z-10 right-6 mt-3 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200",
                        isInCollection
                          ? "bg-green-500 border-green-500 text-white cursor-not-allowed"
                          : isSelected
                          ? "bg-rose-pastel-500 border-rose-pastel-500 text-white shadow-soft"
                          : "bg-white/90 border-rose-pastel-200 text-rose-pastel-500"
                      )}
                    >
                      <Check size={16} />
                    </div>
                  )}
                  <ColorSwatch
                    color={marker}
                    locked={false}
                    showLockIcons={false}
                    onSelect={!enableBulkSelect ? () => handleResultClick(marker) : undefined}
                    ohuhuMode={ohuhuMode}
                    compact={true}
                    replaceMode={enableBulkSelect && isSelected}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {enableBulkSelect && (
          <div className="px-4 py-4 border-t border-slate-100 bg-white/95 backdrop-blur-sm shadow-inner flex-shrink-0">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-500 hidden sm:block">
                  {t("collectionPage.search.selectionLabel")}
                </p>
                <p className="text-sm font-semibold text-slate-700 hidden sm:block">
                  {selectedCount
                    ? t("collectionPage.search.selectionSummary").replace("{count}", selectedCount)
                    : t("collectionPage.search.selectionEmpty")}
                </p>
                <p className="text-xs font-semibold text-slate-600 sm:hidden">
                  {selectedCount
                    ? t("collectionPage.search.selectionSummary").replace("{count}", selectedCount)
                    : t("collectionPage.search.selectionEmpty")}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handleAddBrandCollection}
                  disabled={isAdding || !canAddAllMarkers || brandMarkersToAdd.length === 0}
                  className="inline-flex items-center justify-center px-5 py-2 rounded-2xl bg-gradient-to-r from-emerald-400 to-emerald-500 text-white text-sm font-semibold shadow-soft disabled:opacity-40 disabled:cursor-not-allowed hover:from-emerald-300 hover:to-emerald-400"
                >
                  {t("collectionPage.search.addAllMarkers")}
                </button>
                <button
                  type="button"
                  onClick={handleAddSelected}
                  disabled={isAdding || selectedCount === 0}
                  className="inline-flex items-center justify-center px-5 py-2 rounded-2xl bg-gradient-to-r from-rose-pastel-400 to-rose-pastel-500 text-white text-sm font-semibold shadow-soft disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isAdding && selectedCount > 0
                    ? t("collectionPage.search.addingSelected")
                    : t("collectionPage.search.addSelected").replace("{count}", selectedCount)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
